/**
 * Which renderer an open view tab gets (`specs/M4-V1-views.md` §5, acceptance R2/R3).
 *
 * `null` means "the table view", which is what every board opened on before M4 and what the
 * implicit main table still is. Anything else names a registry entry, and an entry we do not
 * recognise still resolves — to the placeholder, carrying monday's raw type string, so an
 * imported `FeatureBoardView` renders an honest screen instead of an error (R3).
 */
import { viewTypeRegistry, viewTypeForMondayType } from '@elizmonday/shared';

export interface ResolvedViewKind {
  registryId: string | null;
  title: string;
  implemented: boolean;
  plannedIn: string | null;
  mondayType: string | null;
}

export interface ViewTabRow {
  id: string | null;
  registryId: string | null;
  type: string | null;
  name: string;
}

export function resolveViewKind(
  views: readonly ViewTabRow[] | undefined,
  viewId: string | null,
): ResolvedViewKind | null {
  if (views === undefined) return null;
  const view = viewId === null ? views.find((entry) => entry.id === null) : views.find((entry) => entry.id === viewId);
  if (view === undefined) return null;

  const registryId = view.registryId ?? viewTypeForMondayType(view.type) ?? null;
  if (registryId === 'table' || (registryId === null && view.type === null)) return null;

  const definition = registryId === null ? undefined : viewTypeRegistry.find(registryId);
  if (definition === undefined) {
    // An imported view of a kind nobody has registered: still honest, still not an error.
    return {
      registryId,
      title: view.name,
      implemented: false,
      plannedIn: null,
      mondayType: view.type,
    };
  }
  return {
    registryId: definition.id,
    title: definition.title,
    implemented: definition.implemented,
    plannedIn: definition.plannedIn,
    mondayType: view.type,
  };
}
